import {
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";
import {
  Messages,
} from '../../contexts/constants';
import {
  Currency,
  EquipmentType,
  PagePermissions,
  ProjectAdditionalInfo,
  UniqueProcessType,
  RationalizationSummaryInputData,
} from "../../contexts/types";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { resolveDisplayPartNoByKey } from '../../utils/utils';
import RationalizationSummaryInputPopover from "./RationalizationSummaryInputPopover";

/**
 * モーダルのプロパティ
 */
interface Props {
  /** モーダルの表示/非表示 */
  isOpen: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
  /** プロジェクトID */
  projectId: string;
  /** 合理化前後サマリーデータ更新関数 */
  onUpdate: (data: RationalizationSummaryInputData[]) => void;
  /** 設備データ（合理化設備のみ） */
  equipmentData: EquipmentType[];
  /** プロセスコードデータ */
  processCodeData: UniqueProcessType[];
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** 量産開始日（YYYY-MM-DD形式） */
  projectStartDate?: string | null;
  /** EOL予定日（YYYY-MM-DD形式） */
  projectEndDate?: string | null;
  /** 通貨データ */
  currencyData?: Currency[];
  /** ローカル通貨コード（例: JPY, USD） */
  localCurrency?: string | null;
  /** 振り返り日（振り返りモード時のみ指定） */
  reviewDate?: string | null;
  /** 簡易モードか（true時のみ新規必須項目を自動追加） */
  isSimpleMode?: boolean;
  /** PartNoデータ（displayPartNo解決用、No.435対応） */
  partNoData?: { partNo: string; displayPartNo?: string }[];
}

/**
 * 合理化前後サマリーモーダル
 */
const RationalizationSummaryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  projectId,
  onUpdate,
  equipmentData,
  processCodeData,
  pagePermissions,
  projectStartDate,
  projectEndDate,
  currencyData = [],
  localCurrency,
  reviewDate,
  isSimpleMode = false,
  partNoData,
}) => {
  const { t } = useTranslation();
  /** タイトル */
  const TITLE = t('components.RationalizationSummaryModal.title');
  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** トースト表示用 */
  const toast = useToast();
  /**  保存モーダル保存ボタン活性制御 */
  const [isSaving, setIsSaving] = useState(false);
  /** プロジェクト追加情報 */
  const [projectAdditionalInfo, setProjectAdditionalInfo] = useState<ProjectAdditionalInfo>({});
  /** ローカル合理化前後サマリーデータマップ */
  const [rationalizationSummaryMap, setRationalizationSummaryMap] = useState<Map<string, RationalizationSummaryInputData>>(
    new Map()
  );

  /**
   * モーダル表示切替時
   */
  useEffect(() => {
    if (!isOpen) {
      // 閉じた時
      return;
    }

    const fetchData = async () => {
      // プロジェクト追加情報取得
      const projectAdditionalInfo: ProjectAdditionalInfo = await getProjectAdditionalInfoById(projectId);
      setProjectAdditionalInfo(projectAdditionalInfo);

      try {
        if (projectAdditionalInfo.rationalizationSummaryData) {
          // JSONパース
          const rationalizationSummaryData: RationalizationSummaryInputData[] = JSON.parse(projectAdditionalInfo.rationalizationSummaryData);

          // mapデータ変換
          const summaryMap = convertRationalizationSummaryMap(rationalizationSummaryData);
          setRationalizationSummaryMap(summaryMap);

        } else {
          const summaryMap = convertRationalizationSummaryMap([]);
          setRationalizationSummaryMap(summaryMap);
        }
      } catch (error) {
        toast({
          title: TITLE,
          description: t('components.RationalizationSummaryModal.message.fetchDisplayDataFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`${TITLE} 表示データ取得失敗`, [error, projectAdditionalInfo]);
      }
    };

    // プロジェクト追加情報取得実行
    fetchData();

  }, [isOpen]);

  /**
   * 合理化前後サマリーデータをMapデータへ変換
   * @param summaryData 合理化前後サマリーデータ
   * @returns 合理化前後サマリーデータMap（キー: processId）
   */
  const convertRationalizationSummaryMap = (
    summaryData: RationalizationSummaryInputData[]
  ): Map<string, RationalizationSummaryInputData> => {
    const result = new Map<string, RationalizationSummaryInputData>();

    summaryData.forEach(item => {
      if (item.processId) {
        result.set(item.processId, item);
      }
    });

    return result;
  };

  /**
   * 合理化前後サマリーデータ保存ハンドラー
   */
  const handleSaveRationalizationSummary = async (data: RationalizationSummaryInputData): Promise<boolean> => {
    const key = data.processId || "";

    if (isSaving) {
      return false;
    }

    setIsSaving(true);

    if (!key) {
      // IDが特定できない場合
      setIsSaving(false);
      return false;
    }

    const localSummaryMap = rationalizationSummaryMap;

    // 更新用データ
    localSummaryMap.set(key, data);

    // 配列に変換
    const rationalizationSummaryList = Array.from(rationalizationSummaryMap.values());

    // 合理化前後サマリーデータ登録/更新
    const result = await putRationalizationSummary(rationalizationSummaryList);

    if (result) {
      // 遅延実行されるので、別途mapを更新する必要がある
      setRationalizationSummaryMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(key, data);
        return newMap;
      });
    }
    setIsSaving(false);
    return result;
  };

  /**
   * プロジェクト追加情報取得（合理化前後サマリー情報取得）
   * @param projectId プロジェクトID
   * @returns
   */
  const getProjectAdditionalInfoById = async (projectId: string) => {
    try {
      // AppSync
      const payload = {
        query: `
          query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              rationalizationSummaryData
              updateDate
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      };
      // APIリクエスト実行
      const result = await WebAPI.request(payload);
      console.log({ "リクエスト完了": result });
      if (!result?.data?.getProjectAdditionalInfoById) {
        // リクエスト失敗
        toast({
          title: TITLE,
          description: t('components.RationalizationSummaryModal.message.fetchSummaryFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API getProjectAdditionalInfoById. errors = `, result);
      }

      return result?.data?.getProjectAdditionalInfoById;

    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t('components.RationalizationSummaryModal.message.fetchSummaryFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return null;
    };
  };

  /**
   * 合理化前後サマリーデータ保存
   * @param rationalizationSummaryList 合理化前後サマリーデータ
   */
  const putRationalizationSummary = async (rationalizationSummaryList: RationalizationSummaryInputData[]) => {
    try {
      // 登録パラメータ
      const input = {
        projectId: projectId,
        rationalizationSummaryData: JSON.stringify(rationalizationSummaryList),
        updateDate: projectAdditionalInfo?.updateDate,
        ...(reviewDate && { reviewDate }),
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation putRationalizationSummary($input: RationalizationSummaryInput!) {
              putRationalizationSummary(input: $input) {status}
            }
          `,
        variables: {
          input: input,
        }
      });

      console.log(`putRationalizationSummary result = `, result);
      console.log({ "リクエスト完了": result });
      if (result?.data?.putRationalizationSummary.status === 'success') {

        // リクエスト成功
        toast({
          title: TITLE,
          description: t('components.RationalizationSummaryModal.message.saveSuccess'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // データ再取得
        const projectAdditionalInfo: ProjectAdditionalInfo = await getProjectAdditionalInfoById(projectId);
        setProjectAdditionalInfo(projectAdditionalInfo);

        if (projectAdditionalInfo.rationalizationSummaryData) {
          try {
            // JSONパース
            const rationalizationSummaryData = JSON.parse(projectAdditionalInfo.rationalizationSummaryData);

            // mapデータ変換
            const summaryMap = convertRationalizationSummaryMap(rationalizationSummaryData);
            setRationalizationSummaryMap(summaryMap);
          } catch (error) {
            toast({
              title: TITLE,
              description: t('components.RationalizationSummaryModal.message.fetchDisplayDataFailed'),
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            console.error(`${TITLE} 表示データ取得失敗`, [error, projectAdditionalInfo]);
          }
        }

        return true;

      } else if (result?.data?.putRationalizationSummary.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putRationalizationSummary.`);
      } else {
        // リクエスト失敗
        toast({
          title: TITLE,
          description: t('components.RationalizationSummaryModal.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putRationalizationSummary. errors = `, result);
      }

    } catch (error) {
      console.error("データ保存失敗:", error);
      toast({
        title: TITLE,
        description: t('components.RationalizationSummaryModal.message.dataSaveFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    };

    return false;
  };

  /**
   * 閉じるハンドラー
   */
  const handleClose = () => {
    // Mapを配列に変換して親に渡す
    const dataArray = Array.from(rationalizationSummaryMap.values());
    onUpdate(dataArray);
    onClose();
  };

  /**
   * 工程一覧
   */
  const renderProcessTable = () => {

    // 設備の追加がある工程のみをフィルタリング
    const processIdsWithEquipment = new Set(
      equipmentData
        .filter(e => e.processId)
        .map(e => e.processId)
    );

    const filteredProcessData = processCodeData.filter(
      process => processIdsWithEquipment.has(process.uniqueProcessId)
    );

    return (
      <TableContainer border="1px solid" borderColor="gray.200" height="100%" overflowY="unset" overflowX="auto">
        <Table variant="simple" style={{ tableLayout: "fixed" }}>
          <Thead position="sticky" top={0} zIndex={10} backgroundColor="gray.100" boxShadow={`0 -1px 0 #EEEEEE`}>
            <Tr borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="5%" textTransform="none">Seq</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="5%" textTransform="none">Level</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="15%" textTransform="none">ProcessCode</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="17%" textTransform="none">ItemNo</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="15%" textTransform="none">PartNo</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="8%" textTransform="none">CycleTime</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="20%" textTransform="none">RationalizationEquipment</Th>
              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" w="15%" textTransform="none">InputSummary</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredProcessData.map((process) => {
              const existingRationalizationSummary = rationalizationSummaryMap.get(process.uniqueProcessId || "");

              // サイクルタイムJSONをパース
              let cycleTimeData: { partNo: string; cycleTime: number }[] = [];
              if (process.uniqueProcessCycleTimeInSecondByPartNo) {
                cycleTimeData = JSON.parse(process.uniqueProcessCycleTimeInSecondByPartNo);
              }

              // この工程に関連する設備を取得
              const relatedEquipments = equipmentData.filter(
                e => e.processId === process.uniqueProcessId
              );

              return (
                <Tr key={process.uniqueProcessId} _hover={{ bg: "gray.50" }}>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {process.uniqueProcessSeq}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {process.uniqueLevel}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <Text isTruncated title={process.uniqueProcessId || ""}>
                      {process.uniqueProcessId}
                    </Text>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <Text isTruncated title={process.uniqueProcessName || ""}>
                      {process.uniqueProcessName}
                    </Text>
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    {cycleTimeData.map((item, idx) => (
                      <Text key={idx} isTruncated title={resolveDisplayPartNoByKey(item.partNo, partNoData)}>
                        {resolveDisplayPartNoByKey(item.partNo, partNoData)}
                      </Text>
                    ))}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200" textAlign="right">
                    {cycleTimeData.map((item, idx) => (
                      <Text key={idx}>
                        {Number(item.cycleTime).toFixed(1) + " " + t('components.RationalizationSummaryModal.label.secondsSuffix')}
                      </Text>
                    ))}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    {relatedEquipments.map((eq, idx) => (
                      <Text key={idx} isTruncated title={eq.equipmentName || ""}>
                        {eq.equipmentName}
                      </Text>
                    ))}
                  </Td>
                  <Td borderRight="1px solid" borderColor="gray.200">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <RationalizationSummaryInputPopover
                        projectId={projectId}
                        processId={process.uniqueProcessId ?? undefined}
                        itemNo={process.uniqueProcessName ?? undefined}
                        projectStartDate={projectStartDate ?? null}
                        projectEndDate={projectEndDate ?? null}
                        rationalizationSummaryData={existingRationalizationSummary}
                        processCodeData={processCodeData}
                        isSave={isSaving}
                        onSave={handleSaveRationalizationSummary}
                        pagePermissions={pagePermissions}
                        currencyData={currencyData}
                        localCurrency={localCurrency}
                        isSimpleMode={isSimpleMode}
                        partNoData={partNoData}
                      />
                      <Icon
                        as={FaCheckCircle}
                        boxSize={8}
                        mr={2}
                        color={!existingRationalizationSummary ? '#ccc' : 'green'}
                      />
                    </Box>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  /**
   * レンダリング
   */
  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent minW={1700} maxW="95vw">
        <ModalHeader>
          <Text>{TITLE}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text display="flex" justifyContent="flex-start" ml={5} mb={2}>
            {t('components.RationalizationSummaryModal.label.processCount')}: {processCodeData.filter(process =>
              equipmentData.some(e => e.processId === process.uniqueProcessId)
            ).length ?? 0} {t('components.RationalizationSummaryModal.label.count')}
          </Text>
          <Box display="flex" justifyContent="flex-start" h="calc(92vh - 260px)" mt="10px" px="10px">
            <Box width="100%" overflowX="auto">
              {renderProcessTable()}
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} m={1}>
            {t('components.RationalizationSummaryModal.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RationalizationSummaryModal;
